import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, Navigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Container,
} from '@mui/material';
import { Visibility, VisibilityOff, Email as EmailIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import AnimatedLoader from '../../components/common/AnimatedLoader';
import otpService from '../../services/otpService';
import { isAdminEmail } from '../../config/admin';

// Regular login validation schema
const passwordValidationSchema = Yup.object({
  email: Yup.string()
    .required('Email or Username is required'),
  password: Yup.string()
    .min(6, 'Password should be of minimum 6 characters length')
    .required('Password is required'),
});

// OTP login validation schema
const otpValidationSchema = Yup.object({
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email is required'),
  otp: Yup.string()
    .required('OTP is required')
    .length(6, 'OTP must be 6 digits')
    .matches(/^\d+$/, 'OTP must only contain numbers'),
});

// OTP service is now handled by the backend API

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`login-tabpanel-${index}`}
      aria-labelledby={`login-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, completeNewPassword, loading: initialLoading, currentUser, isSigningUp, confirmSignup, resendSignupConfirmation } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // OTP related states
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [emailToVerify, setEmailToVerify] = useState('');
  const [isRequestingOTP, setIsRequestingOTP] = useState(false);

  // Verification (Confirmation) Dialog States
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');

  // New Password Required States
  const [newPasswordDialogOpen, setNewPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [cognitoUserInstance, setCognitoUserInstance] = useState<any>(null);
  const [userAttributes, setUserAttributes] = useState<any>(null);
  const [newPasswordError, setNewPasswordError] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setLoginError('');
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  // Password login form
  const passwordFormik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: passwordValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setLoginError('');
      try {
        const profile = await login(values.email, values.password);

        // DEBUG: Log exactly what we got back
        console.log('LOGIN RESULT - profile:', JSON.stringify(profile));
        console.log('LOGIN RESULT - setupCompleted:', profile?.setupCompleted);
        console.log('LOGIN RESULT - role:', profile?.role);
        console.log('LOGIN RESULT - tenantId:', profile?.tenantId);

        // Explicitly check profile status for redirection
        if (profile) {
          if (profile.newPasswordRequired) {
            setCognitoUserInstance(profile.user);
            setUserAttributes(profile.userAttributes);
            setNewPasswordDialogOpen(true);
            return;
          }

          if (!profile.setupCompleted) {
            console.warn('Profile found but setupCompleted=false! Forcing dashboard for staff...');
            // Staff with tenantId should ALWAYS go to dashboard
            if (profile.tenantId || (profile.role && profile.role !== 'admin' && profile.role !== 'user')) {
              navigate('/dashboard');
            } else {
              navigate('/complete-profile');
            }
          } else if (isAdminEmail(values.email) || profile.role === 'admin') {
            navigate('/admin');
          } else {
            navigate('/dashboard');
          }
        } else {
          // profile is null - log and check auth state
          console.error('LOGIN: profile is null after login()! Redirecting to complete-profile');
          navigate('/complete-profile');
        }
      } catch (error: any) {
        console.error('Login error:', error);
        let errorMessage = 'Failed to login. Please try again.';

        if (error.code === 'UserNotConfirmedException' || error.name === 'UserNotConfirmedException' || error.message?.includes('not confirmed')) {
          // Handle unconfirmed user
          setEmailToVerify(values.email);
          setVerificationDialogOpen(true);
          setIsSubmitting(false);
          return;
        }

        if (error.code === 'auth/user-not-found' || error.code === 'UserNotFoundException') {
          setLoginError('No account found with this email.');
        } else if (error.code === 'auth/wrong-password' || error.message?.includes('Incorrect username or password')) {
          setLoginError('Invalid email or password. Please try again.');
        } else if (error.code === 'auth/too-many-requests' || error.code === 'LimitExceededException') {
          setLoginError('Too many failed attempts. Please try again later.');
        } else {
          // Show the raw error for debugging purposes
          setLoginError(`Login failed: ${error.message || JSON.stringify(error)}`);
          console.error('Detailed Login Error:', error);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // OTP login form
  const otpFormik = useFormik({
    initialValues: {
      email: '',
      otp: '',
    },
    validationSchema: otpValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setLoginError('');
      setOtpError('');

      try {
        // Verify OTP with backend
        const verifyResult = await otpService.verifyOTP(values.email, values.otp);

        if (!verifyResult.success) {
          setLoginError(verifyResult.error || 'Invalid OTP. Please check and try again.');
          return;
        }

        // OTP verified successfully - proceed with login
        // Note: You may need to adjust this based on your auth system
        // This assumes you have a way to login with OTP verification
        await login(values.email, 'OTP-based-login');
        navigate('/dashboard');
      } catch (error: any) {
        console.error('Login error:', error);
        setLoginError('Failed to login with OTP. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Request OTP function
  const requestOtp = async () => {
    const email = otpFormik.values.email;
    if (!email || otpFormik.errors.email) {
      otpFormik.setFieldTouched('email', true);
      return;
    }

    setIsRequestingOTP(true);
    setLoginError('');
    setOtpError('');

    try {
      setEmailToVerify(email);

      // Request OTP from backend
      const result = await otpService.requestOTP(email, 'email');

      if (!result.success) {
        setLoginError(result.error || 'Failed to send OTP. Please try again.');
        return;
      }

      // In development mode, OTP might be returned (for testing)
      if (process.env.NODE_ENV === 'development' && result.otp) {
        console.log('🔑 Development OTP:', result.otp);
      }

      setOtpDialogOpen(true);
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      setLoginError('Failed to send OTP. Please try again.');
    } finally {
      setIsRequestingOTP(false);
    }
  };

  // Handle OTP verification
  const verifyOtp = () => {
    setOtpDialogOpen(false);
    otpFormik.setFieldValue('otp', otp);
  };

  // Handle Confirmation Code Verification (Post-Signup)
  const verifyConfirmationCode = async () => {
    try {
      setVerificationError('');
      await confirmSignup(emailToVerify, confirmationCode);
      setVerificationDialogOpen(false);
      // Auto login or ask user to login again?
      // Since we have the password in formik, we could try to login again automatically
      // But clear security practice is to ask user to login again or just click login button again.
      // For UX, let's just close dialog and show success message.
      setLoginError('');
      alert('Account confirmed successfully! Please log in.');
      // Or we can try to re-submit formik to auto login
      passwordFormik.handleSubmit();
    } catch (error: any) {
      console.error('Verification failed', error);
      setVerificationError(error.message || 'Verification failed');
    }
  };

  const resendConfirmation = async () => {
    try {
      await resendSignupConfirmation(emailToVerify);
      alert('Code resent!');
    } catch (error: any) {
      console.error('Resend failed', error);
      setVerificationError(error.message || 'Resend failed');
    }
  };

  // Handle resending OTP
  const resendOtp = async () => {
    if (!emailToVerify) return;

    setIsRequestingOTP(true);
    setOtpError('');

    try {
      const result = await otpService.resendOTP(emailToVerify, 'email');

      if (!result.success) {
        setOtpError(result.error || 'Failed to resend OTP. Please try again.');
        return;
      }

      // In development mode, OTP might be returned (for testing)
      if (process.env.NODE_ENV === 'development' && result.otp) {
        console.log('🔑 Development OTP (resend):', result.otp);
      }

      setOtpError('');
    } catch (error: any) {
      console.error('Error resending OTP:', error);
      setOtpError('Failed to resend OTP. Please try again.');
    } finally {
      setIsRequestingOTP(false);
    }
  };

  const handleCompleteNewPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setNewPasswordError('Password must be at least 6 characters long');
      return;
    }
    
    setIsSubmitting(true);
    setNewPasswordError('');
    
    try {
      // Cognito forbids modifying read-only attributes (email, email_verified, custom:*)
      // during completeNewPasswordChallenge — strip them out before submitting.
      const sanitizedAttributes = Object.fromEntries(
        Object.entries(userAttributes || {}).filter(([key]) =>
          !['email', 'email_verified', 'phone_number_verified'].includes(key) &&
          !key.startsWith('custom:')
        )
      );
      const profile = await completeNewPassword(cognitoUserInstance, newPassword, sanitizedAttributes);
      setNewPasswordDialogOpen(false);
      // Staff members (invited users with a tenantId) need to set up their profile first
      // Check if this was an invited staff login by seeing if the user attributes had a tenant_id
      const hasTenantId = userAttributes && (userAttributes['custom:tenant_id'] || userAttributes['tenantId']);
      if (hasTenantId) {
        navigate('/staff-onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Failed to set new password', err);
      setNewPasswordError(err.message || 'Failed to set permanent password');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (initialLoading) {
    return <AnimatedLoader fastLoad={true} duration={800} />;
  }

  if (currentUser && !isSigningUp) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <Box>
      <Typography variant="h5" align="center" fontWeight="medium" gutterBottom>
        Login to Your Account
      </Typography>

      {loginError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loginError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab label="Password" />
          <Tab label="OTP Login" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <form onSubmit={passwordFormik.handleSubmit}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email or Username"
            variant="outlined"
            margin="normal"
            value={passwordFormik.values.email}
            onChange={passwordFormik.handleChange}
            onBlur={passwordFormik.handleBlur}
            error={passwordFormik.touched.email && Boolean(passwordFormik.errors.email)}
            helperText={passwordFormik.touched.email && passwordFormik.errors.email ? String(passwordFormik.errors.email) : undefined}
          />
          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            margin="normal"
            value={passwordFormik.values.password}
            onChange={passwordFormik.handleChange}
            onBlur={passwordFormik.handleBlur}
            error={passwordFormik.touched.password && Boolean(passwordFormik.errors.password)}
            helperText={passwordFormik.touched.password && passwordFormik.errors.password ? String(passwordFormik.errors.password) : undefined}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              sx={{ textDecoration: 'none' }}
            >
              Forgot password?
            </Link>
          </Box>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              mt: 1,
              mb: 3,
              fontWeight: 'medium',
              backgroundImage: 'var(--primary-gradient)',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Log In'}
          </Button>
        </form>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <form onSubmit={otpFormik.handleSubmit}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email Address"
            variant="outlined"
            margin="normal"
            value={otpFormik.values.email}
            onChange={otpFormik.handleChange}
            onBlur={otpFormik.handleBlur}
            error={otpFormik.touched.email && Boolean(otpFormik.errors.email)}
            helperText={otpFormik.touched.email && otpFormik.errors.email ? String(otpFormik.errors.email) : undefined}
          />

          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={requestOtp}
            disabled={!otpFormik.values.email || Boolean(otpFormik.errors.email) || isRequestingOTP}
            sx={{
              mt: 1,
              py: 1.2,
              mb: 2
            }}
          >
            {isRequestingOTP ? <CircularProgress size={24} /> : 'Request OTP'}
          </Button>

          <TextField
            fullWidth
            id="otp"
            name="otp"
            label="Enter 6-digit OTP"
            variant="outlined"
            margin="normal"
            value={otpFormik.values.otp}
            onChange={otpFormik.handleChange}
            onBlur={otpFormik.handleBlur}
            error={otpFormik.touched.otp && Boolean(otpFormik.errors.otp)}
            helperText={otpFormik.touched.otp && otpFormik.errors.otp ? String(otpFormik.errors.otp) : undefined}
            inputProps={{ maxLength: 6 }}
          />

          <Button
            fullWidth
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            disabled={isSubmitting || !otpFormik.values.otp}
            sx={{
              py: 1.5,
              mt: 2,
              mb: 3,
              fontWeight: 'medium',
              backgroundImage: 'var(--primary-gradient)',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Log In with OTP'}
          </Button>
        </form>
      </TabPanel>

      <Box mt={3} textAlign="center">
        <Typography variant="body2">
          Don't have an account?{' '}
          <Link
            component={RouterLink}
            to="/signup"
            sx={{ textDecoration: 'none', fontWeight: 'medium' }}
          >
            Sign up
          </Link>
        </Typography>
      </Box>

      {/* OTP Dialog for Login */}
      <Dialog open={otpDialogOpen} onClose={() => setOtpDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            OTP Verification
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            We've sent a 6-digit OTP to <strong>{emailToVerify}</strong>. Please enter it below to login.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="6-digit OTP"
            fullWidth
            variant="outlined"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            error={Boolean(otpError)}
            helperText={otpError}
            inputProps={{ maxLength: 6 }}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={resendOtp}
              disabled={isRequestingOTP}
              sx={{ textTransform: 'none' }}
            >
              {isRequestingOTP ? 'Sending...' : 'Resend OTP'}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtpDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={verifyOtp}
            variant="contained"
            disabled={otp.length !== 6}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Unconfirmed Users */}
      <Dialog open={verificationDialogOpen} onClose={() => setVerificationDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            Account Confirmation Required
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Your account ({emailToVerify}) is not confirmed yet. Please enter the verification code sent to your email.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Confirmation Code"
            fullWidth
            variant="outlined"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            error={Boolean(verificationError)}
            helperText={verificationError}
          />
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={resendConfirmation} sx={{ textTransform: 'none' }}>
              Resend Code
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationDialogOpen(false)}>Cancel</Button>
          <Button onClick={verifyConfirmationCode} variant="contained" disabled={confirmationCode.length < 1}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Password Required Dialog */}
      <Dialog open={newPasswordDialogOpen} onClose={() => {}} disableEscapeKeyDown>
        <DialogTitle>
          Set Permanent Password
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are logging in with a temporary password. Please set a permanent password to continue.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={Boolean(newPasswordError)}
            helperText={newPasswordError}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setNewPasswordDialogOpen(false);
              setLoginError('Login cancelled');
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCompleteNewPassword} 
            variant="contained" 
            disabled={isSubmitting || newPassword.length < 6}
          >
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default Login; 